import React, { useState } from 'react';
import { TextField, Button, Grid, MenuItem, Typography, Alert, Box } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'api/axiosConfig';

const createWorkOrder = async (data) => {
  const { data: res } = await axios.post('/workorders', data);
  return res;
};

const fetchClients = async () => {
  const { data } = await axios.get('/clients');
  return data;
};

const fetchVehicles = async (clientId) => {
  if (!clientId) return [];
  const { data } = await axios.get('/vehicles', { params: { clientId } });
  return data;
};

const WorkOrderForm = ({ mechanics, onSuccess, onCancel }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'maintenance',
    clientId: '',
    vehicleId: '',
    mechanicId: '',
    startTime: new Date(),
    endTime: new Date(new Date().setHours(new Date().getHours() + 2)),
    notes: ''
  });
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  const { data: clients } = useQuery({ queryKey: ['clients'], queryFn: fetchClients });
  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', form.clientId],
    queryFn: () => fetchVehicles(form.clientId),
    enabled: !!form.clientId
  });

  const mutation = useMutation({
    mutationFn: createWorkOrder,
    onSuccess: () => {
      queryClient.invalidateQueries(['workorders']);
      onSuccess();
    },
    onError: (err) => setError(err.response?.data?.message || 'Failed to create work order')
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(form);
  };

  console.log('Mechanics in form:', mechanics);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Typography variant="h5" mb={2}>Create Work Order</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Title" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" multiline rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Type" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
              <MenuItem value="maintenance">Maintenance</MenuItem>
              <MenuItem value="repair">Repair</MenuItem>
              <MenuItem value="installation">Installation</MenuItem>
              <MenuItem value="removal">Removal</MenuItem>
              <MenuItem value="inspection">Inspection</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Client" required value={form.clientId} onChange={e => setForm({...form, clientId: e.target.value, vehicleId: ''})}>
              <MenuItem value="">Select client</MenuItem>
              {clients?.map(client => <MenuItem key={client._id} value={client._id}>{client.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Vehicle" required value={form.vehicleId} onChange={e => setForm({...form, vehicleId: e.target.value})} disabled={!form.clientId}>
              <MenuItem value="">Select vehicle</MenuItem>
              {vehicles?.map(vehicle => <MenuItem key={vehicle._id} value={vehicle._id}>{vehicle.licensePlate} - {vehicle.brand} {vehicle.model}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Mechanic" value={form.mechanicId} onChange={e => setForm({...form, mechanicId: e.target.value})}>
              <MenuItem value="">Unassigned</MenuItem>
              {mechanics?.map(mech => <MenuItem key={mech._id} value={mech._id}>{mech.fullName}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <DateTimePicker label="Start Time" value={form.startTime} onChange={(v) => setForm({...form, startTime: v})} slotProps={{ textField: { fullWidth: true } }} />
          </Grid>
          <Grid item xs={6}>
            <DateTimePicker label="End Time" value={form.endTime} onChange={(v) => setForm({...form, endTime: v})} slotProps={{ textField: { fullWidth: true } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Notes" multiline rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={onCancel}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={mutation.isPending}>{mutation.isPending ? 'Creating...' : 'Create'}</Button>
          </Grid>
        </Grid>
      </form>
    </LocalizationProvider>
  );
};

export default WorkOrderForm;